"use client";

import Image from "next/image";
import Link from "next/link";
import { useInView } from "react-intersection-observer";

export const Services = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  return (
    <div
      id="service"
      ref={ref}
      className="w-full flex items-center justify-center"
    >
      <div className="relative flex flex-col gap-12 md:gap-16 items-center justify-center w-full px-6 py-12 lg:px-0 lg:py-28">
        {/* Pattern */}
        <div
          className={`absolute flex items-center top-[10%] right-[3vw] -z-50 transition-all duration-1000 ease-out
            ${inView ? "scale-100" : "scale-0"}
          `}
        >
          <svg
            width="135"
            height="135"
            viewBox="0 0 135 135"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              opacity="0.35"
              d="M67.5 0L74.6822 33.4462C77.5733 46.9097 88.0903 57.4267 101.554 60.3179L135 67.5L101.554 74.6822C88.0903 77.5733 77.5733 88.0903 74.6822 101.554L67.5 135L60.3178 101.554C57.4267 88.0903 46.9097 77.5733 33.4462 74.6822L0 67.5L33.4462 60.3178C46.9097 57.4267 57.4267 46.9097 60.3179 33.4462L67.5 0Z"
              fill="#FFBD58"
            />
          </svg>
        </div>
        {/* Heading */}
        <div className="flex flex-col gap-1.5 items-center justify-start w-full text-center">
          <h2 className="text-base md:text-xl text-natura-darker-1 font-serifText">
            Services
          </h2>
          <h2 className="text-3xl md:text-4xl lg:text-5xl text-natura-primary font-serifText px-6">
            Our Special Treatment for You
          </h2>
        </div>

        {/* Service Cards */}
        <div className="flex flex-wrap flex-col xl:flex-row gap-6 items-center justify-center">
          {/* Slice One */}
          <div className="flex flex-wrap gap-6 max-w-[448px] order-2 xl:order-1 items-center justify-center">
            {servicesCardData.slice(0, 4).map((s) => (
              <div key={s.id}>
                <Link
                  href={`${s.link}`}
                  className="relative w-[156px] sm:w-[212px] h-[128px] sm:h-[161px] rounded-[10px] bg-gradient-to-br from-[#CD7CDA] to-[#A535B9] flex items-center justify-center hover:bg-gradient-to-br hover:from-natura-primary hover:to-natura-primary"
                >
                  {/* Decor */}
                  <div className="absolute right-0 top-0 size-2/3 mix-blend-difference opacity-25 pt-3">
                    <div className="relative w-full h-full">
                      <Image
                        src={`/services/svg/${s.svgImg}`}
                        alt="icon-image"
                        fill
                        className="object-contain"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex flex-col w-full h-full items-start justify-center p-[14px] gap-2.5">
                    <h5 className="font-serifText text-white text-base/snug sm:text-2xl/snug">
                      {s.title}
                    </h5>
                    {s.desc.length > 0 && (
                      <ul className="list-disc font-nunito text-natura-white text-xs/normal sm:text-sm/normal pl-5">
                        {s.desc.map((p, pid) => (
                          <li key={`${s.title}-${pid}`}>{p}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Link>
              </div>
            ))}
          </div>

          {/* Main Service */}
          <Link
            href={"/services/full-mouth-rehabilitation"}
            className="group flex flex-col gap-3 max-w-[325px] rounded-[10px] overflow-hidden bg-gradient-to-br from-[#CD7CDA] to-[#A535B9] hover:bg-gradient-to-br hover:from-natura-primary hover:to-natura-primary flex-shrink-0 order-1 xl:order-2"
          >
            <div className="size-[325px] relative overflow-hidden">
              <Image
                src={"/services/full-mouth-rehabilitation.webp"}
                alt=""
                fill
                className="object-cover object-center group-hover:scale-125 transition-all duration-500 ease-in-out"
              />
            </div>
            <div className="flex flex-col gap-3 px-[18px] pb-[18px]">
              <h5 className="font-serifText text-white text-base/snug sm:text-2xl/snug">
                Full Mouth Rehabilitation
              </h5>
              <p className="line-clamp-2 font-nunito text-natura-white text-xs/normal sm:text-sm/normal">
                Layanan perawatan komprehensif menyeluruh untuk mengatasi
                berbagai masalah gigi.
              </p>
            </div>
          </Link>

          {/* Slice Two */}
          <div className="flex flex-wrap gap-6 max-w-[448px] order-3 items-center justify-center">
            {servicesCardData.slice(5, 9).map((s) => (
              <div key={s.id} className="order-3">
                <Link
                  href={`${s.link}`}
                  className={`relative w-[156px] sm:w-[212px] h-[128px] sm:h-[161px] rounded-[10px] flex items-center justify-center 
                    ${
                      s.id === 8
                        ? "bg-gradient-to-br from-[#FFBD58] to-[#D99329] hover:bg-gradient-to-br hover:from-natura-accent-dark hover:to-natura-accent-dark"
                        : "bg-gradient-to-br from-[#CD7CDA] to-[#A535B9] hover:bg-gradient-to-br hover:from-natura-primary hover:to-natura-primary"
                    }
                  `}
                >
                  {/* Decor */}
                  <div className="absolute right-0 top-0 size-2/3 mix-blend-difference opacity-25 pt-3">
                    <div className="relative w-full h-full">
                      <Image
                        src={`/services/svg/${s.svgImg}`}
                        alt="icon-image"
                        fill
                        className="object-contain"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex flex-col w-full h-full items-start justify-center p-[14px] gap-2.5">
                    <h5 className="font-serifText text-white text-base/snug sm:text-2xl/snug">
                      {s.title}
                    </h5>
                    {s.desc.length > 0 && (
                      <ul className="list-disc font-nunito text-natura-white text-xs/normal sm:text-sm/normal pl-5">
                        {s.desc.map((p, pid) => (
                          <li key={`${s.title}-${pid}`}>{p}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const servicesCardData = [
  {
    id: 0,
    title: "Scaling Gigi",
    desc: [],
    img: "",
    link: "/services/scaling-gigi",
    svgImg: "scaling_gigi.svg",
  },
  {
    id: 1,
    title: "Bedah Mulut",
    desc: ["Cabut Gigi", "Pencabutan Gigi Bungsu"],
    img: "",
    link: "/services/bedah-mulut",
    svgImg: "bedah_mulut.svg",
  },
  {
    id: 2,
    title: "Behel Gigi",
    desc: ["Behel Metal Standar", "Behel Self Ligating", "Aligner"],
    img: "",
    link: "/services/behel-gigi",
    svgImg: "behel_gigi.svg",
  },
  {
    id: 3,
    title: "Gigi Tiruan",
    desc: ["Crown Gigi", "Gigi Tiruan Lepasan"],
    img: "",
    link: "/services/gigi-palsu",
    svgImg: "gigi_tiruan.svg",
  },
  {
    id: 4,
    title: "Full Mouth Rehabilitation",
    desc: [
      "Layanan perawatan komprehensif untuk mengatasi berbagai masalah gigi",
    ],
    img: "image_pasien_treatment",
    link: "",
    svgImg: "",
  },
  {
    id: 5,
    title: "Konservasi Gigi",
    desc: ["Tambal Gigi", "Veneer Gigi", "Perawatan Saluran Akar"],
    img: "",
    link: "/services/konservasi-gigi",
    svgImg: "konservasi_gigi.svg",
  },
  {
    id: 6,
    title: "Pedodonti Holistik",
    desc: ["Perawatan Gigi Anak"],
    img: "",
    link: "/services/pedodonti-holistik",
    svgImg: "pedodonti_holistik.svg",
  },
  {
    id: 7,
    title: "Bleaching Gigi",
    desc: [],
    img: "",
    link: "/services/bleaching-gigi",
    svgImg: "bleaching_gigi.svg",
  },
  {
    id: 8,
    title: "Lihat Semua",
    desc: [],
    img: "",
    link: "/services",
    svgImg: "arrow_right.svg",
  },
];
